<script setup lang="ts">
  import { ref } from 'vue'
  import { useForm } from 'vee-validate'
  import { toTypedSchema } from '@vee-validate/zod'
  import { z } from 'zod'
  import { Button, Input, Icon, FormControl, FormField, FormItem, toast } from '@rapoo/element'
  import { useLocaleStore } from '@/stores'
  import { userGetValidateCode, userMatchValidateCode, userResetPassword } from '@/service/api/user'

  const emit = defineEmits<{
    (e: 'cancel'): void
    (e: 'success'): void
  }>()

  const showPassword = ref(false)
  const countdown = ref(0)
  let timer: ReturnType<typeof setInterval> | null = null
  const { t } = useLocaleStore()

  const codeValidationCache = new Map<string, boolean>()

  const formSchema = toTypedSchema(
    z
      .object({
        email: z
          .string({ message: t('home.user.validate.email') })
          .email({ message: t('home.user.validate.emailValidFirst') }),
        code: z.string({ message: t('home.user.validate.captcha') }),
        password: z
          .string({ message: t('home.user.validate.newPassword') })
          .regex(/^[a-zA-Z0-9!?@#￥%&*()]+$/, { message: t('home.user.validate.passwordValid') })
          .min(6, { message: t('home.user.validate.minPassword') })
          .max(20, { message: t('home.user.validate.maxPassword') }),
      })
      .superRefine(async (data, ctx) => {
        if (!data.email) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['code'],
            message: t('home.user.validate.emailFirst'),
          })
          return
        }

        if (!z.string().email().safeParse(data.email).success) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['code'],
            message: t('home.user.validate.emailValidFirst'),
          })
          return
        }

        if (!data.code) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['code'],
            message: t('home.user.validate.captcha'),
          })
          return
        }

        if (data.code.length < 4) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['code'],
            message: t('home.user.validate.captchaFailed'),
          })
          return
        }

        const cacheKey = `${data.email}:${data.code}`
        if (codeValidationCache.has(cacheKey)) {
          if (!codeValidationCache.get(cacheKey)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['code'],
              message: t('home.user.validate.captchaFailed'),
            })
          }
          return
        }

        try {
          const { code } = await userMatchValidateCode({ email: data.email, captcha: data.code })
          const isValid = code === 0
          codeValidationCache.set(cacheKey, isValid)

          if (!isValid) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['code'],
              message: t('home.user.validate.captchaFailed'),
            })
          }
        } catch (_) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['code'],
            message: t('home.user.validate.captchaError'),
          })
        }
      }),
  )

  const { handleSubmit, meta, values, validateField } = useForm({
    validationSchema: formSchema,
    initialValues: {
      email: '',
      code: '',
      password: '',
    },
  })

  const sendCode = async () => {
    if (countdown.value > 0) return

    const { valid } = await validateField('email')
    if (!valid) return

    try {
      const { code } = await userGetValidateCode({
        email: values.email!,
      })

      if (code === 0) {
        toast.success(t('home.user.forgotPasswordForm.sendCaptchaSuccessful'))
        countdown.value = 60
        timer = setInterval(() => {
          countdown.value--
          if (countdown.value <= 0 && timer) {
            clearInterval(timer)
            timer = null
          }
        }, 1000)
      }
    } catch (error) {
      toast.error(t('home.user.forgotPasswordForm.sendCaptchaFailed'))
      console.error('Failed to send verification code:', error)
    }
  }

  const onSubmit = handleSubmit(async (values) => {
    const { code: resCode } = await userResetPassword({
      email: values.email!,
      captcha: values.code!,
      password: values.password!,
    })

    if (resCode === 0) {
      toast.success(t('home.user.forgotPasswordForm.resetPasswordSuccessful'))
      emit('success')
    }
  })
</script>

<template>
  <div>
    <h2 class="text-4xl-semibold text-center mb-8">
      {{ $t('home.user.forgotPasswordForm.title') }}
    </h2>
    <form id="forgot-password-form" class="space-y-3" autocomplete="off" @submit="onSubmit">
      <FormField v-slot="{ componentField, errorMessage }" name="email">
        <FormItem>
          <FormControl>
            <Input
              class="h-12 rounded-9xl"
              type="text"
              :placeholder="$t('home.user.emailPlaceholder')"
              v-bind="componentField"
              autocomplete="email"
              :error-message="errorMessage"
            >
              <template #prefix>
                <Icon icon="Multimedia-Wireframe-Mail" class="size-4" />
              </template>
            </Input>
          </FormControl>
        </FormItem>
      </FormField>

      <div class="flex gap-2 items-start">
        <div class="flex-1">
          <FormField v-slot="{ componentField, errorMessage }" name="code">
            <FormItem>
              <FormControl>
                <Input
                  class="h-12 rounded-9xl"
                  type="text"
                  :placeholder="$t('home.user.captchaPlaceholder')"
                  v-bind="componentField"
                  autocomplete="one-time-code"
                  :error-message="errorMessage"
                />
              </FormControl>
            </FormItem>
          </FormField>
        </div>
        <Button
          type="button"
          color="blue1"
          class="shrink-0 h-12 w-31.75"
          :disabled="countdown > 0"
          @click="sendCode"
        >
          {{ countdown > 0 ? `${countdown}s` : $t('home.user.forgotPasswordForm.sendCaptcha') }}
        </Button>
      </div>

      <FormField v-slot="{ componentField, errorMessage }" name="password">
        <FormItem>
          <FormControl>
            <Input
              class="h-12 rounded-9xl"
              :type="showPassword ? 'text' : 'password'"
              :placeholder="$t('home.user.newPasswordPlaceholder')"
              v-bind="componentField"
              autocomplete="new-password"
              :error-message="errorMessage"
              :tooltip="$t('home.user.validate.passwordValid')"
            >
              <template #prefix>
                <Icon icon="Function-Wireframe-Password" class="size-4" />
              </template>
              <template #suffix>
                <Icon
                  :icon="showPassword ? 'Function-Wireframe-View' : 'Function-Wireframe-Hide'"
                  class="size-4 cursor-pointer pointer-events-auto"
                  @click="showPassword = !showPassword"
                />
              </template>
            </Input>
          </FormControl>
        </FormItem>
      </FormField>

      <div class="flex gap-4 mt-8">
        <Button type="button" color="text-b-b" class="w-full h-12" @click="emit('cancel')">
          {{ $t('common.cancel') }}
        </Button>
        <Button type="submit" class="w-full h-12" :disabled="!meta.valid">
          {{ $t('common.confirm2') }}
        </Button>
      </div>
    </form>
  </div>
</template>
